import type { RequestConfig } from '../types';

/** Cliente HTTP mínimo (get). */
export interface ManagementDashboardApiClient {
  get<T>(url: string, config?: RequestConfig): Promise<T>;
}

export interface ManagementDashboardUsersKpi {
  totalActiveUsers: number;
  activeUsersGrowthPercent: number;
}

export interface ManagementDashboardActiveCoursesKpi {
  totalActiveCourses: number;
  activeCoursesDeltaVsPreviousMonth: number;
}

export interface ManagementDashboardCompletionRateKpi {
  completionRatePercent: number | null;
  completionRateDeltaVsPreviousMonth: number | null;
}

export interface ManagementDashboardAssessmentScoreKpi {
  averageAssessmentScorePercent: number;
  assessmentScoreDeltaVsPreviousMonth: number;
  minimumPassingScore: number;
}

export interface ManagementDashboardAnalyticsKpisResponse {
  usersKpi: ManagementDashboardUsersKpi;
  activeCoursesKpi: ManagementDashboardActiveCoursesKpi;
  completionRateKpi: ManagementDashboardCompletionRateKpi;
  assessmentScoreKpi: ManagementDashboardAssessmentScoreKpi;
}

/** GET v1/ManagementDashboard/assessment-stats */
export interface ManagementDashboardAssessmentStatsResponse {
  passedAttempts: number;
  failedAttempts: number;
  pendingUserCourses: number;
  passedAttemptsPercent: number;
  failedAttemptsPercent: number;
  pendingUserCoursesPercent: number;
  minimumPassingScore: number;
}

/** GET v1/ManagementDashboard/leaderboard-preview */
export interface ManagementDashboardLeaderboardPreviewItem {
  rank: number;
  userId: string;
  name: string;
  activePoints: number;
}

export interface ManagementDashboardLeaderboardPreviewResponse {
  items: ManagementDashboardLeaderboardPreviewItem[];
}

/**
 * GET v1/ManagementDashboard/engagement-overview
 * Contrato provisório — alinhar com o backend quando o endpoint estabilizar.
 */
export interface ManagementDashboardEngagementOverviewPoint {
  monthLabel?: string;
  totalUsers?: number;
  activeUsers?: number;
}

export interface ManagementDashboardEngagementOverviewMonth {
  referenceDateUtc?: string;
  totalActiveUsers?: number;
  monthlyActiveUsersWithInteraction?: number;
}

export interface ManagementDashboardEngagementOverviewResponse {
  /** Preferencial: pontos ordenados no tempo (Jan, Feb, …). */
  points?: ManagementDashboardEngagementOverviewPoint[];
  /** Alternativas comuns de API — mapear no hook se necessário. */
  months?: string[] | ManagementDashboardEngagementOverviewMonth[];
  totalUsersSeries?: number[];
  activeUsersSeries?: number[];
}

/**
 * GET v1/ManagementDashboard/course-performance
 * Item de categoria — nomes de campo podem variar até o contrato final.
 */
export interface ManagementDashboardCoursePerformanceCategory {
  name?: string;
  categoryName?: string;
  completionPercent?: number;
  inProgressPercent?: number;
}

export interface ManagementDashboardCoursePerformanceResponse {
  categories: ManagementDashboardCoursePerformanceCategory[];
}

const ANALYTICS_KPIS_PATH = 'v1/ManagementDashboard/analytics-kpis';
const ASSESSMENT_STATS_PATH = 'v1/ManagementDashboard/assessment-stats';
const LEADERBOARD_PREVIEW_PATH = 'v1/ManagementDashboard/leaderboard-preview';
const ENGAGEMENT_OVERVIEW_PATH = 'v1/ManagementDashboard/engagement-overview';
const COURSE_PERFORMANCE_PATH = 'v1/ManagementDashboard/course-performance';

const plainTextHeaders = { Accept: 'text/plain' } as const;

/**
 * Admin — Management Dashboard (KPIs e widgets analíticos)
 */
export function createManagementDashboardApi(api: ManagementDashboardApiClient) {
  return {
    async fetchAnalyticsKpis(): Promise<ManagementDashboardAnalyticsKpisResponse> {
      return api.get<ManagementDashboardAnalyticsKpisResponse>(
        `/${ANALYTICS_KPIS_PATH}`,
        {
          headers: plainTextHeaders,
        }
      );
    },

    async fetchAssessmentStats(): Promise<ManagementDashboardAssessmentStatsResponse> {
      return api.get<ManagementDashboardAssessmentStatsResponse>(
        `/${ASSESSMENT_STATS_PATH}`,
        {
          headers: plainTextHeaders,
        }
      );
    },

    async fetchLeaderboardPreview(): Promise<ManagementDashboardLeaderboardPreviewResponse> {
      return api.get<ManagementDashboardLeaderboardPreviewResponse>(
        `/${LEADERBOARD_PREVIEW_PATH}`,
        {
          headers: plainTextHeaders,
        }
      );
    },

    async fetchEngagementOverview(): Promise<ManagementDashboardEngagementOverviewResponse> {
      return api.get<ManagementDashboardEngagementOverviewResponse>(
        `/${ENGAGEMENT_OVERVIEW_PATH}`,
        {
          headers: plainTextHeaders,
        }
      );
    },

    async fetchCoursePerformance(): Promise<ManagementDashboardCoursePerformanceResponse> {
      return api.get<ManagementDashboardCoursePerformanceResponse>(
        `/${COURSE_PERFORMANCE_PATH}`,
        {
          headers: plainTextHeaders,
        }
      );
    },
  };
}

export type ManagementDashboardApi = ReturnType<
  typeof createManagementDashboardApi
>;
